import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Monitor, Boxes, Info, Database, LayoutGrid, Download, Smartphone,
  Filter, BarChart2, PieChart, Calculator, Target, Clock,
  Crown, FileSignature, Users, UserCog, Search, ArrowRight,
  TrendingUp, SlidersHorizontal
} from "lucide-react";

/**
 * Command Palette - Modern Cmd+K style quick navigation
 * Allows users to quickly search and navigate to any section
 */
const CommandPalette = ({ isOpen, onClose, role, type, category, canAccess, hasAccessToAny, push }) => {
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  // All available commands with their metadata
  const allCommands = useMemo(() => {
    const commands = [];

    // Puntos de Venta
    if (canAccess(role, type, category, 'machines')) {
      commands.push({
        id: 'machines',
        label: 'ONEs',
        description: 'Gestión de puntos de venta ONE',
        category: 'Puntos de Venta',
        icon: <Monitor size={18} />,
        path: '/machines',
        keywords: ['one', 'maquina', 'machine', 'punto', 'venta']
      });
    }
    if (canAccess(role, type, category, 'minis')) {
      commands.push({
        id: 'minis',
        label: 'MINIs',
        description: 'Gestión de puntos de venta MINI',
        category: 'Puntos de Venta',
        icon: <Boxes size={18} />,
        path: '/minis',
        keywords: ['mini', 'maquina', 'machine', 'punto', 'venta']
      });
    }

    // Operativa
    if (canAccess(role, type, category, 'incidences')) {
      commands.push({
        id: 'incidences',
        label: 'Incidencias',
        description: 'Gestión de incidencias',
        category: 'Operativa',
        icon: <Info size={18} />,
        path: '/incidences',
        keywords: ['incidencia', 'problema', 'issue']
      });
    }
    if (canAccess(role, type, category, 'audit')) {
      commands.push({
        id: 'audit',
        label: 'Audit',
        description: 'Auditoría del sistema',
        category: 'Operativa',
        icon: <Database size={18} />,
        path: '/audit',
        keywords: ['audit', 'auditoria', 'log']
      });
    }
    if (canAccess(role, type, category, 'modules')) {
      commands.push({
        id: 'modules',
        label: 'Módulos',
        description: 'Gestión de módulos',
        category: 'Operativa',
        icon: <LayoutGrid size={18} />,
        path: '/modules',
        keywords: ['modulo', 'module', 'componente']
      });
    }
    if (canAccess(role, type, category, 'matrixs')) {
      commands.push({
        id: 'matrixs',
        label: 'Versiones',
        description: 'Gestión de versiones',
        category: 'Operativa',
        icon: <Download size={18} />,
        path: '/matrixs',
        keywords: ['version', 'matrix', 'firmware']
      });
    }
    if (canAccess(role, type, category, 'tpvs')) {
      commands.push({
        id: 'tpvs',
        label: 'TPVs',
        description: 'Gestión de terminales de pago',
        category: 'Operativa',
        icon: <Smartphone size={18} />,
        path: '/tpvs',
        keywords: ['tpv', 'terminal', 'pago', 'payment']
      });
    }

    // Ventas
    if (canAccess(role, type, category, 'sales')) {
      commands.push({
        id: 'sales',
        label: 'Análisis',
        description: 'Análisis de ventas',
        category: 'Ventas',
        icon: <Filter size={18} />,
        path: '/sales',
        keywords: ['venta', 'sale', 'analisis', 'reporte']
      });
    }
    if (canAccess(role, type, category, 'arqueos')) {
      commands.push({
        id: 'arqueos',
        label: 'Gráficos',
        description: 'Gráficos de arqueos',
        category: 'Ventas',
        icon: <BarChart2 size={18} />,
        path: '/arqueos',
        keywords: ['grafico', 'arqueo', 'chart']
      });
      commands.push({
        id: 'arqueossales',
        label: 'KPI',
        description: 'Indicadores clave de rendimiento',
        category: 'Ventas',
        icon: <PieChart size={18} />,
        path: '/arqueossales',
        keywords: ['kpi', 'indicador', 'metric']
      });
    }
    if (canAccess(role, type, category, 'objectives')) {
      commands.push({
        id: 'objectives',
        label: 'Objetivos',
        description: 'Gestión de objetivos',
        category: 'Ventas',
        icon: <Target size={18} />,
        path: '/objectives',
        keywords: ['objetivo', 'target', 'goal']
      });
    }
    if (canAccess(role, type, category, 'comissions')) {
      commands.push({
        id: 'comissions',
        label: 'Comisiones',
        description: 'Gestión de comisiones',
        category: 'Ventas',
        icon: <Calculator size={18} />,
        path: '/comissions',
        keywords: ['comision', 'commission', 'pago']
      });
    }
    if (canAccess(role, type, category, 'quicksight')) {
      commands.push({
        id: 'quicksight',
        label: 'QuickSight',
        description: 'Dashboard de QuickSight',
        category: 'Ventas',
        icon: <TrendingUp size={18} />,
        path: '/quicksight',
        keywords: ['quicksight', 'dashboard', 'analytics']
      });
    }

    // Fichajes
    if (canAccess(role, type, category, 'shifts')) {
      commands.push({
        id: 'shifts',
        label: 'Jornada Promotor',
        description: 'Control de jornada de promotores',
        category: 'Fichajes',
        icon: <Clock size={18} />,
        path: '/shifts',
        keywords: ['fichaje', 'jornada', 'promotor', 'shift']
      });
    }
    // Removed Jornada Trabajador command
    // if (canAccess(role, type, category, 'shiftsAdmin')) {
    //   commands.push({
    //     id: 'shiftsadmin',
    //     label: 'Jornada Trabajador',
    //     description: 'Control de jornada de trabajadores',
    //     category: 'Fichajes',
    //     icon: <Clock size={18} />,
    //     path: '/shiftsadmin',
    //     keywords: ['fichaje', 'jornada', 'trabajador', 'shift', 'admin']
    //   });
    // }

    // Administración
    if (canAccess(role, type, category, 'customers')) {
      commands.push({
        id: 'customers',
        label: 'Clientes',
        description: 'Gestión de clientes',
        category: 'Administración',
        icon: <Crown size={18} />,
        path: '/customers',
        keywords: ['cliente', 'customer', 'empresa']
      });
    }
    if (canAccess(role, type, category, 'contracts')) {
      commands.push({
        id: 'contracts',
        label: 'Contratos',
        description: 'Gestión de contratos',
        category: 'Administración',
        icon: <FileSignature size={18} />,
        path: '/contracts',
        keywords: ['contrato', 'contract', 'acuerdo']
      });
    }
    if (canAccess(role, type, category, 'promoters')) {
      commands.push({
        id: 'promoters',
        label: 'Promotores',
        description: 'Gestión de promotores',
        category: 'Administración',
        icon: <Users size={18} />,
        path: '/promoters',
        keywords: ['promotor', 'promoter', 'vendedor']
      });
    }
    if (canAccess(role, type, category, 'users')) {
      commands.push({
        id: 'users',
        label: 'Trabajadores',
        description: 'Gestión de trabajadores',
        category: 'Administración',
        icon: <UserCog size={18} />,
        path: '/users',
        keywords: ['trabajador', 'user', 'empleado', 'admin']
      });
    }
    if (canAccess(role, type, category, 'logistics')) {
      commands.push({
        id: 'logistics',
        label: 'Logística',
        description: 'Gestión de logística',
        category: 'Administración',
        icon: <Boxes size={18} />,
        path: '/logistics',
        keywords: ['logistica', 'logistics', 'envio', 'shipping']
      });
    }

    return commands;
  }, [role, type, category, canAccess]);

  // Filter commands based on search
  const filteredCommands = useMemo(() => {
    if (!search.trim()) return allCommands;
    
    const searchLower = search.toLowerCase();
    return allCommands.filter(cmd => {
      const matchLabel = cmd.label?.toLowerCase().includes(searchLower);
      const matchDescription = cmd.description?.toLowerCase().includes(searchLower);
      const matchCategory = cmd.category?.toLowerCase().includes(searchLower);
      const matchKeywords = cmd.keywords?.some(k => k.toLowerCase().includes(searchLower));
      
      return matchLabel || matchDescription || matchCategory || matchKeywords;
    });
  }, [search, allCommands]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups = {};
    filteredCommands.forEach(cmd => {
      const category = cmd.category || 'General';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(cmd);
    });
    return groups;
  }, [filteredCommands]);

  // Reset selection when filtered commands change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredCommands.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          handleNavigate(filteredCommands[selectedIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands, onClose]);

  const handleNavigate = (cmd) => {
    if (cmd.path && push) {
      push(cmd.path);
    }
    onClose();
    setSearch("");
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="command-palette-backdrop"
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 2000,
          animation: 'fadeIn 0.15s ease'
        }}
      />

      {/* Palette */}
      <div 
        className="command-palette"
        style={{
          position: 'fixed',
          top: '15vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90%',
          maxWidth: '640px',
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          zIndex: 2001,
          overflow: 'hidden',
          animation: 'slideDown 0.2s ease'
        }}
      >
        <div 
          className="command-palette-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#6b7280', flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="command-palette-input"
            placeholder="Buscar acciones, páginas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar comando"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '16px',
              color: '#111827',
              background: 'transparent'
            }}
          />
          <kbd 
            className="command-palette-kbd"
            style={{
              padding: '4px 8px',
              background: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#6b7280',
              fontFamily: 'monospace',
              fontWeight: 600
            }}
          >
            ESC
          </kbd>
        </div>

        <div 
          className="command-palette-content"
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: '8px'
          }}
        >
          {filteredCommands.length === 0 ? (
            <div 
              className="command-palette-empty"
              style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '14px'
              }}
            >
              No se encontraron resultados para "{search}"
            </div>
          ) : (
            Object.entries(groupedCommands).map(([category, cmds]) => (
              <div key={category} className="command-palette-group" style={{ marginBottom: '12px' }}>
                <div 
                  className="command-palette-group-label"
                  style={{
                    padding: '8px 12px 4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: '#6b7280',
                    letterSpacing: '0.05em'
                  }}
                >
                  {category}
                </div>
                {cmds.map((cmd) => {
                  const globalIndex = filteredCommands.indexOf(cmd);
                  const isSelected = globalIndex === selectedIndex;
                  
                  return (
                    <button
                      key={cmd.id}
                      className={`command-palette-item ${isSelected ? "selected" : ""}`}
                      onClick={() => handleNavigate(cmd)}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '10px 12px',
                        border: 'none',
                        background: isSelected ? '#f3f4f6' : 'transparent',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.1s ease',
                        transform: isSelected ? 'translateX(2px)' : 'none'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                        {cmd.icon && (
                          <span style={{ color: '#6b7280', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                            {typeof cmd.icon === 'function' ? <cmd.icon size={18} /> : cmd.icon}
                          </span>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827', marginBottom: '2px' }}>
                            {cmd.label}
                          </div>
                          {cmd.description && (
                            <div style={{ fontSize: '12px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {cmd.description}
                            </div>
                          )}
                        </div>
                      </div>
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        style={{ 
                          color: '#9ca3af', 
                          flexShrink: 0,
                          opacity: isSelected ? 1 : 0,
                          transition: 'opacity 0.15s ease'
                        }}
                      >
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div 
          className="command-palette-footer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 20px',
            borderTop: '1px solid #e5e7eb',
            background: '#f9fafb',
            fontSize: '12px',
            color: '#6b7280'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px', fontFamily: 'monospace', fontWeight: 600 }}>↑</kbd>
            <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px', fontFamily: 'monospace', fontWeight: 600 }}>↓</kbd>
            <span>para navegar</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px', fontFamily: 'monospace', fontWeight: 600 }}>↵</kbd>
            <span>para seleccionar</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '11px', fontFamily: 'monospace', fontWeight: 600 }}>ESC</kbd>
            <span>para cerrar</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommandPalette;
